import React, { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { cardTitle, cardArtFallback } from '../../NFTCollection/components/CardInspectModal';

type Row = {
  id: number | string;
  nftName: string;
  nftType: string;
  nftRarity: string;
  seller: string;
  buyer: string;
  priceInINC: string;
  status: string;
  date?: string | null;
};

type Props = {
  rows: Row[];
  cloudfrontBase?: string;
  formatRarity: (rarity: string | undefined | null) => string;
  getRarityColor: (rarity: string | undefined | null) => string;
};

export const ActivityGrid: React.FC<Props> = ({
  rows,
  cloudfrontBase,
  formatRarity,
  getRarityColor
}) => {
  const visibleRows = useMemo(
    () => rows.filter((r) => r.status !== 'fee_pending'),
    [rows]
  );

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: 'nftName',
        headerName: 'Digital Collectible',
        width: 180,
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              minWidth: 0
            }}
          >
            <img
              src={
                cloudfrontBase
                  ? `${cloudfrontBase}/${params.row.nftType}/${params.row.nftName}/${params.row.nftRarity}.png`
                  : ''
              }
              alt={params.row.nftName}
              style={{
                width: 40,
                height: 40,
                borderRadius: 4,
                flexShrink: 0,
                objectFit: 'cover'
              }}
              onError={(e) => {
                if (!cardArtFallback(e.target as HTMLImageElement))
                  (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <Box sx={{ minWidth: 0, display: 'grid', gap: 0.15 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  lineHeight: 1.05,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {cardTitle(params.row.nftName)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  lineHeight: 1.05,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {params.row.nftType}
              </Typography>
            </Box>
          </Box>
        )
      },
      {
        field: 'nftRarity',
        headerName: 'Rarity',
        width: 100,
        renderCell: (params) => (
          <Chip
            label={formatRarity(params.value)}
            size="small"
            sx={{
              backgroundColor: getRarityColor(params.value),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        )
      },
      { field: 'seller', headerName: 'Seller', width: 120 },
      { field: 'buyer', headerName: 'Buyer', width: 120 },
      {
        field: 'priceInINC',
        headerName: 'Price (INC)',
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight="bold" color="#38e8ff">
            {params.value} INC
          </Typography>
        )
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => {
          if (params.value === 'fee_pending') return null;

          const niceStatus = String(params.value)
            .replace('_', ' ')
            .toLowerCase()
            .replace(/^\w/, (c) => c.toUpperCase());

          return (
            <Chip
              label={niceStatus}
              size="small"
              color={
                params.value === 'sold'
                  ? 'success'
                  : params.value === 'active'
                    ? 'primary'
                    : 'default'
              }
              variant="outlined"
            />
          );
        }
      },
      {
        field: 'date',
        headerName: 'Date',
        width: 150,
        renderCell: (params) => {
          const v = params.value as string | null | undefined;
          if (!v) return <Typography variant="body2">—</Typography>;
          return (
            <Typography variant="body2">
              {format(new Date(v), 'MMM dd, yyyy HH:mm')}
            </Typography>
          );
        }
      }
    ],
    [cloudfrontBase, formatRarity, getRarityColor]
  );

  return (
    <Box
      sx={{
        height: { xs: 400, sm: 500, md: 600 },
        width: '100%',
        overflow: 'auto'
      }}
    >
      <DataGrid
        rows={visibleRows}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 }
          }
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        sx={{
          height: '100%',
          '& .MuiDataGrid-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          },
          '& .MuiDataGrid-cell': {
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            display: 'flex',
            alignItems: 'center',
            py: 0.5
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(56, 232, 255, 0.2)',
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.2)'
          },
          '& .MuiTablePagination-root': {
            color: 'white'
          },
          '& .MuiIconButton-root': {
            color: 'white'
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            height: 'auto !important'
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            display: 'flex',
            alignItems: 'center'
          }
        }}
      />
    </Box>
  );
};
